/**
 * Marks the product details child that holds a Klarna placement with `data-klarna-empty` while Klarna has nothing to
 * show for it. Klarna then renders only "No content found for the given configuration" into its open shadow root,
 * e.g. before the visitor accepts cookies (consentmanager). CSS cannot read the shadow root; with the attribute,
 * component-product-info.css drops the empty block's gap and does not join it with the points row.
 * Rendered by blocks/_product-details.liquid only when the column contains a <klarna-placement>.
 */
class KlarnaMessageState extends HTMLElement {
  #observers = [];
  #timers = [];
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    customElements.whenDefined('klarna-placement').then(() => {
      if (signal.aborted) return;
      this.closest('.shopify-block__product-details')
        ?.querySelectorAll('klarna-placement')
        .forEach((placement) => this.#watch(placement, signal));
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observers.forEach((observer) => observer.disconnect());
    this.#timers.forEach((timer) => clearTimeout(timer));
    this.#observers = [];
    this.#timers = [];
  }

  /**
   * @param {HTMLElement} placement
   * @param {AbortSignal} signal
   * @param {number} [attempt] - Klarna may attach its shadow root a moment after the element upgrades.
   */
  #watch(placement, signal, attempt = 0) {
    if (signal.aborted) return;
    const block = placement.closest('.shopify-block__product-details > *');
    const shadow = placement.shadowRoot;
    if (!block) return;
    if (!shadow) {
      if (attempt < 20) this.#timers.push(setTimeout(() => this.#watch(placement, signal, attempt + 1), 250));
      return;
    }

    const update = () => {
      const hasMessage = Boolean(shadow.querySelector('[part="osm-message"]')?.textContent.trim());
      block.toggleAttribute('data-klarna-empty', !hasMessage && shadow.innerHTML.includes('No content found'));
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(shadow, { childList: true, subtree: true, characterData: true });
    this.#observers.push(observer);
  }
}

if (!customElements.get('klarna-message-state')) {
  customElements.define('klarna-message-state', KlarnaMessageState);
}
